// Landing "Fiyatlar": the one per-seat plan (backend billing/plans.py via
// /api/billing/plans/, with built-in fallbacks so the section never renders
// empty) and today's TCMB rate as a rough ₺ hint.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, endpoints } from '../../../config/api';
import { useT } from '../../../i18n';
import { Reveal } from './primitives';
import './PricingSection.css';

const FALLBACK = { price_per_seat_month: '2.00', yearly_price_per_seat_month: '1.60', yearly_discount_percent: 20, vat_percent: 20 };

const messages = {
  monthly: { tr: 'Aylık', en: 'Monthly' },
  yearly: { tr: 'Yıllık', en: 'Yearly' },
  save: { tr: '%{n} indirim', en: '{n}% off' },
  plan: { tr: 'Pro', en: 'Pro' },
  planLead: { tr: 'Tüm özellikler, tek fiyat.', en: 'Every feature, one price.' },
  perUser: { tr: '/ kullanıcı / ay', en: '/ user / month' },
  billedMonthly: { tr: 'Aylık faturalanır', en: 'Billed monthly' },
  billedYearly: {
    tr: ({ total }) => `Yıllık $${total} olarak faturalanır`,
    en: ({ total }) => `Billed yearly at $${total}`,
  },
  vatNote: { tr: 'Fiyatlara %{n} KDV dahil değildir.', en: 'Prices exclude {n}% VAT.' },
  tryHint: {
    tr: ({ amount }) => `Bugünkü TCMB kuruyla yaklaşık ₺${amount} / kullanıcı / ay. Ödeme TL olarak alınır.`,
    en: ({ amount }) => `About ₺${amount} / user / month at today’s TCMB rate. You’re charged in Turkish lira.`,
  },
  features: {
    tr: [
      'Sınırsız imza şablonu ve gruplara göre otomatik atama',
      'Outlook masaüstü, web, yeni Outlook, iOS ve Android',
      'Kampanya banner’ları ve içerik takvimi',
      'Memnuniyet anketi ve olumsuz geri bildirim alarmı',
      'Yedi dilde şablon varyantı',
      'Teslimat günlüğü ve yasal uyarı metinleri',
    ],
    en: [
      'Unlimited signature templates, assigned by group',
      'Outlook desktop, web, new Outlook, iOS and Android',
      'Campaign banners and a content calendar',
      'Satisfaction survey with negative-feedback alerts',
      'Template variants in seven languages',
      'Delivery log and legal disclaimers',
    ],
  },
  cta: { tr: '14 gün ücretsiz deneyin', en: 'Try it free for 14 days' },
  ctaNote: {
    tr: 'Kredi kartı gerekmez · Deneme süresi ilk imzanız gönderildiğinde başlar',
    en: 'No credit card · Your trial starts when your first signature goes out',
  },
  enterprise: { tr: 'Çok sayıda kullanıcı ya da özel ihtiyaçlar mı?', en: 'Lots of users or special requirements?' },
  talk: { tr: 'Bizimle konuşun →', en: 'Talk to us →' },
};

export function PricingSection({ onDemo }) {
  const { t, formatNumber } = useT(messages);
  const [cycle, setCycle] = useState('yearly');
  const [pricing, setPricing] = useState(FALLBACK);
  const [rate, setRate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(endpoints.billing.plans)
      .then((res) => {
        if (cancelled) return;
        setPricing({ ...FALLBACK, ...res });
        if (res.exchange_rate) setRate(Number(res.exchange_rate.usd_try));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const perMonth = cycle === 'yearly' ? pricing.yearly_price_per_seat_month : pricing.price_per_seat_month;
  const yearlyTotal = (Number(pricing.yearly_price_per_seat_month) * 12).toFixed(2);
  const tryPerMonth = rate
    ? formatNumber(Number(perMonth) * rate * (1 + pricing.vat_percent / 100), { maximumFractionDigits: 0 })
    : null;

  return (
    <div className="lp-pricing">
      <Reveal>
        <div className="lp-pricing-toggle" role="radiogroup">
          {['monthly', 'yearly'].map((c) => (
            <button key={c} type="button" role="radio" aria-checked={cycle === c}
              className={cycle === c ? 'is-on' : ''} onClick={() => setCycle(c)}>
              {t(c)}
              {c === 'yearly' && <span className="lp-pricing-save">{t('save', { n: pricing.yearly_discount_percent })}</span>}
            </button>
          ))}
        </div>
      </Reveal>

      <Reveal className="lp-pricing-card" delay={0.1}>
        <div className="lp-pricing-head">
          <h3>{t('plan')}</h3>
          <p>{t('planLead')}</p>
        </div>
        <div className="lp-pricing-price">
          <span className="lp-pricing-amount">${perMonth}</span>
          <span className="lp-pricing-unit">{t('perUser')}</span>
        </div>
        <p className="lp-pricing-billed">
          {cycle === 'yearly' ? t('billedYearly', { total: yearlyTotal }) : t('billedMonthly')}
          {' · '}{t('vatNote', { n: pricing.vat_percent })}
        </p>
        {tryPerMonth && <p className="lp-pricing-try">{t('tryHint', { amount: tryPerMonth })}</p>}
        <ul className="lp-pricing-features">
          {t('features').map((f) => <li key={f}>{f}</li>)}
        </ul>
        <Link to="/basla" className="lp-btn lp-btn-lg lp-pricing-cta">
          {t('cta')} <span className="lp-btn-arrow">→</span>
        </Link>
        <p className="lp-pricing-note">{t('ctaNote')}</p>
      </Reveal>

      <Reveal className="lp-pricing-enterprise" delay={0.2}>
        <span>{t('enterprise')}</span>
        <button type="button" onClick={onDemo}>{t('talk')}</button>
      </Reveal>
    </div>
  );
}

export default PricingSection;
